import { AxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import { Identifier, useDataProvider, useNotify, useRedirect } from "react-admin";
import { FinlandGroupTypeEnum, SiteContryEnum } from "shared/enums/globalEnums";
import { SiteResourcesEnum } from "shared/enums/resourceMappings";
import useOpenState from "shared/hooks/useOpenState";
import { buildMetricsPayload, getErrorMessageCause, normalizeAssociationValues } from "../helpers";
import { fetchSelectedGroupQuery, postAssociationBatchQuery, sendModbusMetricsQuery } from "../queries";
import {
  AssociationCreatePayload,
  FinlandSiteServiceInfo,
  GroupSiteAssociationFormValues,
  NewSitesMetrics,
  SiteMetricPayload,
} from "../../types";

type AssociationErrorResponse = {
  detail?: string;
};

export const useCreateForm = ({ country }: { country: SiteContryEnum }) => {
  const [associationValues, setAssociationValues] = useState<AssociationCreatePayload | null>(null);
  const [metricValues, setMetricValues] = useState<NewSitesMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [siteFinlandServiceInfo, setFinlandSiteServiceInfo] = useState<FinlandSiteServiceInfo>({
    selectedSitesIds: null,
    group: null,
  });
  const dataprovider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const { open, handleOpen, handleClose: handleSimpleClose } = useOpenState();

  const handleClose = useCallback(() => {
    handleSimpleClose();
    setMetricValues(null);
  }, [handleSimpleClose]);

  const displayAssociationOperationsSummaryOnly = useMemo(
    () =>
      (country === SiteContryEnum.France && associationValues) ||
      (country === SiteContryEnum.Finland &&
        associationValues &&
        associationValues.groupType === FinlandGroupTypeEnum.fingrid_ro),
    [country, associationValues],
  );

  const displayAssociationAndMetricOperationsSummary = useMemo(
    () => country === SiteContryEnum.Finland && Boolean(metricValues),
    [country, metricValues],
  );

  const displayPeMetricsForm = useMemo(
    () =>
      country === SiteContryEnum.Finland &&
      associationValues &&
      associationValues.groupType === FinlandGroupTypeEnum.fingrid_pe &&
      !metricValues,
    [country, associationValues, metricValues],
  );

  const sendModbusMetrics = useCallback(
    async (metricsPayload: SiteMetricPayload[]) => {
      try {
        await sendModbusMetricsQuery(metricsPayload);
      } catch (err) {
        notify("Error creating many metrics. The Sites will not be updated :\n " + getErrorMessageCause(err), {
          type: "error",
        });
      }
    },
    [notify],
  );

  const postAssociations = useCallback(
    async (associationPayload: AssociationCreatePayload) => {
      try {
        if (country === SiteContryEnum.Finland) {
          associationPayload.group_ids = [associationPayload.group_id as Identifier];
          delete associationPayload.group_id;
        }
        await postAssociationBatchQuery(associationPayload);
        notify("Associations created successfully", { type: "success" });
        redirect("list", SiteResourcesEnum.Associations);
      } catch (err) {
        const axiosErr = err as AxiosError<AssociationErrorResponse>;
        notify(`Error creating associations. ${axiosErr.response?.data?.detail}`, {
          type: "error",
        });
      }
    },
    [notify, redirect, country],
  );

  const handleSavePeMetrics = useCallback(
    (newMetricsPayload: NewSitesMetrics) => {
      setMetricValues(newMetricsPayload);
    },
    [setMetricValues],
  );

  const handleSubmitPeMetrics = useCallback(async () => {
    const groupCode = siteFinlandServiceInfo.group?.code;
    if (!groupCode || !associationValues || !metricValues) return;

    const newMetrics = buildMetricsPayload(metricValues, groupCode);
    await sendModbusMetrics(newMetrics);
  }, [metricValues, siteFinlandServiceInfo.group?.code, sendModbusMetrics, associationValues]);

  const handleFinalSubmit = useCallback(async () => {
    setIsLoading(true);
    try {
      if (country === SiteContryEnum.Finland && metricValues) {
        await handleSubmitPeMetrics();
      }
    } catch (e: unknown) {
      notify(`An error occurred while creating associations: ${getErrorMessageCause(e)}`, { type: "error" });
      setIsLoading(false);
      return;
    }
    try {
      if (associationValues) {
        await postAssociations(normalizeAssociationValues(associationValues));
      }
    } catch (e: unknown) {
      notify(`An error occurred while creating associations: ${getErrorMessageCause(e)}`, { type: "error" });
    }
    setIsLoading(false);
  }, [country, handleSubmitPeMetrics, postAssociations, notify, associationValues, metricValues]);

  const handleSubmitAssociations = useCallback(
    async (values: GroupSiteAssociationFormValues) => {
      const { site_ids, group_id } = values;

      if (country === SiteContryEnum.Finland && group_id) {
        try {
          setIsLoading(true);
          const finlandGroup = await fetchSelectedGroupQuery(dataprovider, group_id);
          if (String(finlandGroup?.type) === FinlandGroupTypeEnum.fingrid_pe) {
            setFinlandSiteServiceInfo({
              selectedSitesIds: site_ids.map(Number),
              group: finlandGroup,
            });
          }
          setIsLoading(false);
        } catch (e: unknown) {
          notify(`An error occurred while creating associations: ${getErrorMessageCause(e)}`, { type: "error" });
        }
      }
      setAssociationValues(values);
      handleOpen();
    },
    [country, dataprovider, notify, handleOpen],
  );

  return {
    handleSavePeMetrics,
    handleClose,
    handleSubmitAssociations,
    handleFinalSubmit,
    siteFinlandServiceInfo,
    open,
    isLoading,
    metricValues,
    associationValues,
    displayAssociationOperationsSummaryOnly,
    displayAssociationAndMetricOperationsSummary,
    displayPeMetricsForm,
  };
};
